import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../config';
import { useFeature } from '../context/AuthContext';
import { toastSuccess, toastError, errorMessage } from '../lib/toast';
import { REFUSALS } from '../lib/permissionErrors';
import { formatDate, formatTime } from '../lib/formatDate';
import ConfirmModal from './ConfirmModal';
import DepartmentManagersPanel from './admin/DepartmentManagersPanel';
import UserPermissionsToolsPanel from './admin/UserPermissionsPanel';
import AdminUsersTab from './admin/usersTab/AdminUsersTab';
import UserDetailHeader from './admin/usersTab/UserDetailHeader';
import UserDepartmentsSection from './admin/usersTab/UserDepartmentsSection';
import AdminDepartmentsTab from './admin/departmentsTab/AdminDepartmentsTab';
import AdminOverview from './admin/AdminOverview';
import OdooSyncTab from './admin/odooSync/OdooSyncTab';
import AdminVendorsTab from './admin/vendors/AdminVendorsTab';
import ItAppsTab from './admin/itApps/ItAppsTab';
import AdminToolAccessRequests from './admin/AdminToolAccessRequests';
import AppsPipeline from './admin/apps/AppsPipeline';
import AdminSettings from './admin/AdminSettings';
import RolesPage from './admin/roles/RolesPage';
import SsmStatusPill from './admin/apps/SsmStatusPill';
import AdminShell from './admin/shell/AdminShell';
import AdminAccessDenied from './admin/shell/AdminAccessDenied';
import useAdminAccess from '../context/useAdminAccess';
import './admin/apps/apps.css';
import './admin/UserPermissionsPanel.css';
import './admin/usersTab/usersTab.css';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const TOOL_TYPES = [
    { value: 'tool', label: 'Tool' },
    { value: 'document', label: 'Document' },
    { value: 'sheet', label: 'Sheet' },
    { value: 'service', label: 'Service' },
    { value: 'dashboard', label: 'Dashboard' },
];
const TOOL_TYPE_LABEL = Object.fromEntries(TOOL_TYPES.map((t) => [t.value, t.label]));

// Topbar title + subtitle per tab — shown in the AdminShell header so the page
// always announces where you are. Keys match the #tab= hash values.
const TAB_META = {
    overview:      { title: 'Overview',            subtitle: 'Engagement, coverage, and request pipelines at a glance.' },
    users:         { title: 'User Permissions',    subtitle: 'Manage who can access which tools, action items, and policies.' },
    departments:   { title: 'Departments',         subtitle: 'Departments and their default tool grants.' },
    dept_managers: { title: 'Department Managers',  subtitle: 'Assign managers to the departments they oversee.' },
    categories:    { title: 'Categories',          subtitle: 'Organize the tool catalog into categories.' },
    tools:         { title: 'Tools',               subtitle: 'The tool catalog and incoming tool requests.' },
    it_apps:       { title: 'IT apps',             subtitle: 'The apps IT runs, who owns them and how they are billed.' },
    apps:          { title: 'Apps',                subtitle: 'Review, provision, and publish employee-built apps.' },
    activity:      { title: 'Activity Logs',       subtitle: 'Append-only audit trail of admin actions.' },
    vendors:       { title: 'Vendors',            subtitle: 'Specialities, who can access them, and the vendor sync.' },
    sync:          { title: 'Odoo Sync',           subtitle: 'Department mapping and sync history.' },
    settings:      { title: 'Settings',            subtitle: 'Feature toggles and platform-wide configuration.' },
    roles:         { title: 'Roles & Permissions', subtitle: 'Define roles and the app permissions each one grants.' },
};

const VALID_TABS = ['overview', 'users', 'departments', 'dept_managers', 'categories', 'tools', 'it_apps', 'apps', 'activity', 'vendors', 'sync', 'settings', 'roles'];

const AdminDashboard = () => {
    // Active tab is a real route segment: /admin/<tab>. The sub-state (Tools
    // catalog/requests, reqkind) stays in the hash since it's view-local.
    const { tab } = useParams();
    const activeTab = VALID_TABS.includes(tab) ? tab : 'overview';
    const buildAppEnabled = useFeature('build_your_app');
    const rbacOn = useFeature('rbac_management');
    const { isFullAdmin, areas, can, canAny } = useAdminAccess();
    const itAppsOn = useFeature('it_apps');
    // A flag-off deep link is denied like a missing permission (the nav item is hidden too).
    const tabAllowed = (isFullAdmin || areas.includes(activeTab)) && !(activeTab === 'it_apps' && !itAppsOn);

    // Tools sub-tab (catalog | requests). Persisted in #sub= alongside tab.
    const readSubTabFromHash = () => {
        const m = (typeof window !== 'undefined' ? window.location.hash : '').match(/sub=([a-z_]+)/);
        return m && ['catalog', 'requests'].includes(m[1]) ? m[1] : 'catalog';
    };
    const [toolsSubTab, _setToolsSubTab] = useState(readSubTabFromHash);
    const setToolsSubTab = (next) => {
        _setToolsSubTab(next);
        const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        params.set('tab', 'tools');
        params.set('sub', next);
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${params.toString()}`);
    };

    // Pending tool-request count for the badge on the Tools › Requests sub-tab.
    // This is the sum of pending new-tool requests + pending access requests.
    const [pendingNewToolCount, setPendingNewToolCount] = useState(0);
    const [pendingAccessCount, setPendingAccessCount] = useState(0);
    const pendingRequestsCount = pendingNewToolCount + pendingAccessCount;
    const refreshPendingRequestsCount = async () => {
        try {
            const [overview, access] = await Promise.all([
                can('overview.view')
                    ? axios.get(`${API_BASE_URL}/api/admin/overview`, { withCredentials: true })
                    : Promise.resolve({ data: {} }),
                can('tool_access.resolve')
                    ? axios.get(`${API_BASE_URL}/api/admin/tool_access_requests`, {
                        params: { status: 'pending', page: 1, size: 1 },
                        withCredentials: true,
                    })
                    : Promise.resolve({ data: {} }),
            ]);
            setPendingNewToolCount(overview.data?.stats?.requests_pending ?? 0);
            setPendingAccessCount(access.data?.total ?? 0);
        } catch (_) { /* non-fatal */ }
    };

    // Tools › Requests sub-sub-tab: 'access' (new) | 'new_tool' (legacy queue).
    const readReqKindFromHash = () => {
        const m = (typeof window !== 'undefined' ? window.location.hash : '').match(/reqkind=([a-z_]+)/);
        return m && ['access', 'new_tool'].includes(m[1]) ? m[1] : 'access';
    };
    const [reqKind, _setReqKind] = useState(readReqKindFromHash);
    const setReqKind = (next) => {
        _setReqKind(next);
        const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
        params.set('reqkind', next);
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${params.toString()}`);
    };
    // Pending app-request count for the top-level Apps tab badge. The Apps tab
    // body (AppsPipeline) owns its own per-stage counts; this is just the badge.
    const [pendingAppCount, setPendingAppCount] = useState(0);
    const refreshPendingAppCount = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/app_requests/stats`, { withCredentials: true });
            setPendingAppCount(res.data?.pending ?? 0);
        } catch (_) { /* non-fatal */ }
    };

    const [users, setUsers] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [categories, setCategories] = useState([]);
    const [tools, setTools] = useState([]);
    const [actionItems, setActionItems] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [syncingUser, setSyncingUser] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [userDepartments, setUserDepartments] = useState([]);
    const [departmentTools, setDepartmentTools] = useState([]);
    const [departmentToolRows, setDepartmentToolRows] = useState([]); // [{id, tool_id, visibility}]
    const [userActionItems, setUserActionItems] = useState([]);
    const [userPolicies, setUserPolicies] = useState([]);
    const [activityLogs, setActivityLogs] = useState([]);
    const [assignedActionItems, setAssignedActionItems] = useState([]);
    const [assignedPolicies, setAssignedPolicies] = useState([]);

    // Search & Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');

    // Confirm Modal State
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, isDangerous: false });

    // Department modal state
    const [departmentModal, setDepartmentModal] = useState({ open: false, department: null });
    const [departmentForm, setDepartmentForm] = useState({ name: '', description: '' });

    // Category modal state
    const [categoryModal, setCategoryModal] = useState({ open: false, category: null });
    const [categoryForm, setCategoryForm] = useState({ name: '', icon: '' });

    // Tool modal state
    const [toolModal, setToolModal] = useState({ open: false, tool: null });
    const [toolForm, setToolForm] = useState({ name: '', description: '', icon_url: '', link: '', category_id: '', type: 'tool', department_ids: [] });
    const [typeFilter, setTypeFilter] = useState('All');
    // Source filter for the tools catalog: All | self_built (published from a
    // Build-your-app request) | native (everything else).
    const [sourceFilter, setSourceFilter] = useState('All');

    // Action list modal state
    const [actionModal, setActionModal] = useState({ open: false, action: null });
    const [actionForm, setActionForm] = useState({ title: '', description: '' });

    // Policy modal state
    const [policyModal, setPolicyModal] = useState({ open: false, policy: null });
    const [policyForm, setPolicyForm] = useState({ name: '', link: '' });

    // Add user modal state
    const [addUserModal, setAddUserModal] = useState({ open: false });
    const [addUserForm, setAddUserForm] = useState({ email: '', name: '' });
    const [addUserSubmitting, setAddUserSubmitting] = useState(false);
    const [addUserError, setAddUserError] = useState('');

    // Activity logs filter state
    const [logFilters, setLogFilters] = useState({
        action: 'all',
        user: 'all',
        dateFrom: '',
        dateTo: '',
        query: ''
    });
    const [activityLogsPage, setActivityLogsPage] = useState(1);
    const ACTIVITY_LOGS_PAGE_SIZE = 25;

    // Tool requests (admin)
    const [toolRequests, setToolRequests] = useState([]);
    const [toolRequestsPage, setToolRequestsPage] = useState(1);
    const [toolRequestsQuery, setToolRequestsQuery] = useState('');
    const [resolveRequestModal, setResolveRequestModal] = useState({ open: false, request: null, admin_notes: '', category_id: '' });
    const [resolving, setResolving] = useState(false);

    const TOOL_REQUESTS_PAGE_SIZE = 10;
    const filteredToolRequests = useMemo(() => {
        const q = toolRequestsQuery.trim().toLowerCase();
        if (!q) return toolRequests;
        return toolRequests.filter((r) =>
            (r.name || '').toLowerCase().includes(q) ||
            (r.requester_name || '').toLowerCase().includes(q) ||
            (r.requester_email || '').toLowerCase().includes(q)
        );
    }, [toolRequests, toolRequestsQuery]);
    const toolRequestsTotalPages = Math.max(1, Math.ceil(filteredToolRequests.length / TOOL_REQUESTS_PAGE_SIZE));
    const toolRequestsStart = (toolRequestsPage - 1) * TOOL_REQUESTS_PAGE_SIZE;
    const paginatedToolRequests = filteredToolRequests.slice(toolRequestsStart, toolRequestsStart + TOOL_REQUESTS_PAGE_SIZE);

    useEffect(() => {
        if (!tabAllowed) return;
        if (can('users.view')) fetchUsers();
        fetchDepartments();
        fetchCategories();
        fetchTools();
        if (can('users.grants')) { fetchActionItems(); fetchPolicies(); }
        if (canAny(['overview.view', 'tool_access.resolve'])) refreshPendingRequestsCount();
        if (can('apps.view')) refreshPendingAppCount();
        if (activeTab === 'activity' && can('activity.view')) fetchActivityLogs();
        if (activeTab === 'departments' && can('departments.view')) fetchDepartmentsRich();
        // Tool requests live under the Tools tab now (sub-tab = 'requests').
        if (activeTab === 'tools' && toolsSubTab === 'requests' && can('tool_proposals.publish')) fetchToolRequests();
    // Refetch only when the tab changes; the fetchers and `can` are rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, toolsSubTab, tabAllowed]);

    // When a tab's first sub-tab is hidden for this admin, default to the
    // first sub-tab they can actually see.
    useEffect(() => {
        if (activeTab !== 'tools') return;
        if (toolsSubTab === 'catalog' && !can('tools.manage')) setToolsSubTab('requests');
        if (reqKind === 'access' && !can('tool_access.resolve') && can('tool_proposals.publish')) setReqKind('new_tool');
    // Default the sub-tab once on entering Tools, not on every sub-tab change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Reset tool requests page when list shrinks
    useEffect(() => {
        if (filteredToolRequests.length > 0 && toolRequestsStart >= filteredToolRequests.length) setToolRequestsPage(1);
    }, [filteredToolRequests.length, toolRequestsStart]);

    // Reset to page 1 when the requests search query changes
    useEffect(() => { setToolRequestsPage(1); }, [toolRequestsQuery]);

    // Reset search when switching tabs
    useEffect(() => {
        setSearchQuery('');
        setCategoryFilter('All');
        setSelectedUser(null);
    }, [activeTab]);

    // Close the Tool drawer on Escape
    useEffect(() => {
        if (!toolModal.open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setToolModal({ open: false, tool: null });
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [toolModal.open]);

    // Close the Category drawer on Escape
    useEffect(() => {
        if (!categoryModal.open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') setCategoryModal({ open: false, category: null });
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [categoryModal.open]);

    const fetchUsers = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/users`, { withCredentials: true });
            setUsers(res.data);
        } catch (err) {
            console.error('Failed to fetch users', err);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/categories`, { withCredentials: true });
            setCategories(res.data);
        } catch (err) {
            console.error('Failed to fetch categories', err);
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/departments`, { withCredentials: true });
            setDepartments(res.data);
        } catch (err) {
            console.error('Failed to fetch departments', err);
        }
        // Best-effort refresh of the rich rows for the Departments tab table.
        if (can('departments.view')) fetchDepartmentsRich();
    };

    // Rich department rows for the Departments tab table (managers, counts).
    const [departmentsRich, setDepartmentsRich] = useState([]);
    // Filter typed into the tools search inside the department drawer.
    const [deptDrawerToolFilter, setDeptDrawerToolFilter] = useState('');
    const fetchDepartmentsRich = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/admin/departments`, { withCredentials: true });
            setDepartmentsRich(res.data?.departments || []);
        } catch (err) {
            console.error('Failed to fetch department details', err);
        }
    };

    const fetchTools = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/tools`, { withCredentials: true });
            setTools(res.data);
        } catch (err) {
            console.error('Failed to fetch tools', err);
        }
    };

    const fetchActionItems = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/action_items`, { withCredentials: true });
            setActionItems(res.data);
        } catch (err) {
            console.error('Failed to fetch action items', err);
        }
    };

    const fetchPolicies = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/policies`, { withCredentials: true });
            setPolicies(res.data);
        } catch (err) {
            console.error('Failed to fetch policies', err);
        }
    };

    const fetchActivityLogs = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/activity_logs?limit=100`, { withCredentials: true });
            setActivityLogs(res.data.logs || []);
        } catch (err) {
            console.error('Failed to fetch activity logs', err);
        }
    };

    const fetchToolRequests = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/tool_requests`, { withCredentials: true });
            const data = res.data || [];
            setToolRequests(data);
        } catch (err) {
            console.error('Failed to fetch tool requests', err);
        }
    };

    const handleResolveRequest = async () => {
        const { request, admin_notes, category_id } = resolveRequestModal;
        if (!request) return;
        setResolving(true);
        try {
            await axios.patch(`${API_BASE_URL}/api/tool_requests/${request.id}/resolve`, {
                admin_notes: admin_notes.trim() || undefined,
                category_id: category_id ? parseInt(category_id, 10) : undefined,
            }, { withCredentials: true });
            setResolveRequestModal({ open: false, request: null, admin_notes: '', category_id: '' });
            toastSuccess(`${request.name} added to the catalog.`);
            fetchToolRequests();
            if (activeTab === 'tools') fetchTools();
        } catch (err) {
            toastError(errorMessage(err, "Couldn't add the tool to the catalog. Try again.", {
                not_ready_for_admin: 'This proposal was already handled. The list has been refreshed.',
            }));
            // 409: another admin (or an earlier click) already published it, so this
            // dialog is stale. Close it and show the list as it now is.
            if (err?.response?.status === 409) {
                setResolveRequestModal({ open: false, request: null, admin_notes: '', category_id: '' });
                fetchToolRequests();
                if (activeTab === 'tools') fetchTools();
            }
        } finally {
            setResolving(false);
        }
    };

    // Filter Logic

    const filteredCategories = useMemo(() => {
        if (!searchQuery) return categories;
        return categories.filter(c =>
            c.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [categories, searchQuery]);

    const filteredTools = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return tools.filter(t => {
            const matchesSearch = (t.name || '').toLowerCase().includes(q) ||
                (t.description || '').toLowerCase().includes(q);
            const matchesCategory = categoryFilter === 'All'
                || (categoryFilter === 'Uncategorized' && !t.category)
                || t.category === categoryFilter;
            const matchesType = typeFilter === 'All' || (t.type || 'tool') === typeFilter;
            const matchesSource = sourceFilter === 'All'
                || (sourceFilter === 'self_built' && t.self_built)
                || (sourceFilter === 'native' && !t.self_built);
            return matchesSearch && matchesCategory && matchesType && matchesSource;
        });
    }, [tools, searchQuery, categoryFilter, typeFilter, sourceFilter]);

    // Counts per category/type after search applies, so the sidebar reflects
    // what would be visible for each filter when search is the only constraint.
    const toolFacetCounts = useMemo(() => {
        const q = searchQuery.toLowerCase();
        const searched = tools.filter(t =>
            (t.name || '').toLowerCase().includes(q) ||
            (t.description || '').toLowerCase().includes(q)
        );
        const byCategory = { All: searched.length, Uncategorized: 0 };
        const byType = { All: searched.length };
        const bySource = { All: searched.length, self_built: 0, native: 0 };
        for (const t of searched) {
            const cat = t.category || 'Uncategorized';
            byCategory[cat] = (byCategory[cat] || 0) + 1;
            const ty = t.type || 'tool';
            byType[ty] = (byType[ty] || 0) + 1;
            if (t.self_built) bySource.self_built += 1; else bySource.native += 1;
        }
        return { byCategory, byType, bySource };
    }, [tools, searchQuery]);

    // Filter activity logs
    const filteredActivityLogs = useMemo(() => {
        const q = (logFilters.query || '').trim().toLowerCase();
        return activityLogs.filter(log => {
            if (logFilters.action !== 'all' && log.action !== logFilters.action) {
                return false;
            }
            if (logFilters.user !== 'all' && log.user_id?.toString() !== logFilters.user) {
                return false;
            }
            if (logFilters.dateFrom) {
                const logDate = new Date(log.created_at);
                const fromDate = new Date(logFilters.dateFrom);
                if (logDate < fromDate) return false;
            }
            if (logFilters.dateTo) {
                const logDate = new Date(log.created_at);
                const toDate = new Date(logFilters.dateTo);
                toDate.setHours(23, 59, 59, 999);
                if (logDate > toDate) return false;
            }
            if (q) {
                const hay = [
                    log.user_name, log.user_email, log.action,
                    log.entity_type, log.entity_name, log.details,
                ].filter(Boolean).join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [activityLogs, logFilters]);

    const activityLogsTotalPages = Math.max(1, Math.ceil(filteredActivityLogs.length / ACTIVITY_LOGS_PAGE_SIZE));
    const activityLogsStart = (activityLogsPage - 1) * ACTIVITY_LOGS_PAGE_SIZE;
    const paginatedActivityLogs = filteredActivityLogs.slice(activityLogsStart, activityLogsStart + ACTIVITY_LOGS_PAGE_SIZE);

    // Reset to first page when filters change or row count drops below current page
    useEffect(() => { setActivityLogsPage(1); }, [logFilters]);
    useEffect(() => {
        if (activityLogsStart >= filteredActivityLogs.length && activityLogsPage > 1) {
            setActivityLogsPage(1);
        }
    }, [filteredActivityLogs.length, activityLogsStart, activityLogsPage]);

    // Get unique actions from logs
    const uniqueActions = useMemo(() => {
        const actions = new Set(activityLogs.map(log => log.action));
        return Array.from(actions).sort();
    }, [activityLogs]);

    // Get unique users from logs
    const uniqueLogUsers = useMemo(() => {
        const usersMap = new Map();
        activityLogs.forEach(log => {
            if (log.user_id && !usersMap.has(log.user_id)) {
                usersMap.set(log.user_id, {
                    id: log.user_id,
                    name: log.user_name,
                    email: log.user_email
                });
            }
        });
        return Array.from(usersMap.values()).sort((a, b) => a.name?.localeCompare(b.name));
    }, [activityLogs]);

    const handleUserSelect = async (user) => {
        // Refresh user data from server to get latest state
        const userRes = await axios.get(`${API_BASE_URL}/api/users`, { withCredentials: true });
        const freshUser = userRes.data.find(u => u.id === user.id);
        
        setSelectedUser(freshUser || user);
        setAssignedActionItems([]); // Clear immediately to prevent stale data
        setAssignedPolicies([]);
        try {
            // Fetch user departments
            const deptRes = await axios.get(`${API_BASE_URL}/api/user_departments?user_id=${user.id}`, { withCredentials: true });
            setUserDepartments(deptRes.data);
            
            if (can('users.grants')) {
                const actionRes = await axios.get(`${API_BASE_URL}/api/user_action_items?user_id=${user.id}`, { withCredentials: true });
                const actionIds = actionRes.data.map(ua => ua.action_item_id);
                setUserActionItems(actionIds);

                const policyRes = await axios.get(`${API_BASE_URL}/api/user_policies?user_id=${user.id}`, { withCredentials: true });
                const policyIds = policyRes.data.map(up => up.policy_id);
                setUserPolicies(policyIds);

                // Set assigned items from current global state - already sorted by display_order
                const assignedActions = actionItems.filter(action => actionIds.includes(action.id)).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
                const assignedPols = policies.filter(policy => policyIds.includes(policy.id)).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                setAssignedActionItems(assignedActions);
                setAssignedPolicies(assignedPols);
            }
        } catch (err) {
            console.error('Failed to fetch user tools', err);
        }
    };

    const assignActionToUser = async (actionId) => {
        if (!selectedUser) return;
        await axios.post(`${API_BASE_URL}/api/user_action_items`, {
            user_id: selectedUser.id,
            action_item_id: actionId
        }, { withCredentials: true });
        setUserActionItems(prev => [...prev, actionId]);
    };

    const assignPolicyToUser = async (policyId) => {
        if (!selectedUser) return;
        await axios.post(`${API_BASE_URL}/api/user_policies`, {
            user_id: selectedUser.id,
            policy_id: policyId
        }, { withCredentials: true });
        setUserPolicies(prev => [...prev, policyId]);
    };

    const OUTCOME_LABELS = {
        in_sync: 'Already in sync with Odoo.',
        noop: 'No Odoo change (Odoo not configured or no match).',
        add: 'Department added from Odoo.',
        reassign: 'Department reassigned from Odoo.',
        origin_change: 'Odoo origin updated.',
    };

    // Appended to the department outcome. `refreshed` is deliberately silent --
    // saying "documents refreshed" on every sync trains admins to ignore the
    // line, and the cases worth reading are the ones where nothing happened.
    const DOCUMENT_LABELS = {
        no_hr_record: 'No Odoo HR record, so document status was not refreshed.',
        odoo_unavailable: 'Document status could not be read from Odoo.',
        refresh_failed: 'Document status refresh failed.',
    };

    const syncUserFromOdoo = async () => {
        if (!selectedUser || syncingUser) return;
        setSyncingUser(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/api/admin/odoo-sync/user/${selectedUser.id}`,
                {},
                { withCredentials: true }
            );
            // Refresh the user list so departments/HR fields reflect the sync,
            // and re-point the drawer at the fresh record.
            const userRes = await axios.get(`${API_BASE_URL}/api/users`, { withCredentials: true });
            setUsers(userRes.data);
            const fresh = userRes.data.find((u) => u.id === selectedUser.id);
            if (fresh) setSelectedUser(fresh);
            const outcome = res.data?.outcome;
            const base = OUTCOME_LABELS[outcome] || `Synced ${selectedUser.name} from Odoo.`;
            // The document result is appended rather than replacing the
            // department outcome: an admin clicking this usually wants one or
            // the other, and which one is not knowable from here.
            const docs = DOCUMENT_LABELS[res.data?.documents];
            toastSuccess(docs ? `${base} ${docs}` : base);
        } catch (err) {
            toastError(errorMessage(err, 'Could not sync this user from Odoo.', {
                sync_already_running: 'A sync is already running. Try again shortly.',
                user_not_found: 'User no longer exists.',
            }));
        } finally {
            setSyncingUser(false);
        }
    };

    const toggleAdminStatus = async () => {
        if (!selectedUser) return;

        setConfirmModal({
            open: true,
            title: selectedUser.is_admin ? 'Remove Admin Privileges?' : 'Grant Admin Privileges?',
            message: selectedUser.is_admin
                ? `Are you sure you want to remove admin rights from ${selectedUser.name}? They will lose access to this dashboard.`
                : `Are you sure you want to make ${selectedUser.name} an admin? They will have full control over the system.`,
            confirmText: selectedUser.is_admin ? 'Remove Admin' : 'Make Admin',
            isDangerous: selectedUser.is_admin, // red button only for removing admin
            onConfirm: async () => {
                try {
                    const updatedUser = { ...selectedUser, is_admin: !selectedUser.is_admin };
                    const res = await axios.put(`${API_BASE_URL}/api/users/${selectedUser.id}`, updatedUser, { withCredentials: true });

                    // Update local state
                    setSelectedUser(res.data);
                    setUsers(users.map(u => u.id === res.data.id ? res.data : u));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    toastError(errorMessage(err, 'Failed to update admin status', REFUSALS));
                }
            }
        });
    };

    const deactivateUser = async () => {
        if (!selectedUser) return;

        setConfirmModal({
            open: true,
            title: 'Deactivate User?',
            message: `Are you sure you want to deactivate ${selectedUser.name}? They will not be able to log in until reactivated.`,
            confirmText: 'Deactivate',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    const res = await axios.post(`${API_BASE_URL}/api/users/${selectedUser.id}/deactivate`, {}, { withCredentials: true });
                    setSelectedUser(res.data);
                    setUsers(users.map(u => u.id === res.data.id ? res.data : u));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    toastError(errorMessage(err, 'Failed to deactivate user', REFUSALS));
                }
            }
        });
    };

    const reactivateUser = async () => {
        if (!selectedUser) return;

        setConfirmModal({
            open: true,
            title: 'Reactivate User?',
            message: `Reactivate ${selectedUser.name}? They will be able to log in again.`,
            confirmText: 'Reactivate',
            isDangerous: false,
            onConfirm: async () => {
                try {
                    const res = await axios.post(`${API_BASE_URL}/api/users/${selectedUser.id}/reactivate`, {}, { withCredentials: true });
                    setSelectedUser(res.data);
                    setUsers(users.map(u => u.id === res.data.id ? res.data : u));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    toastError(errorMessage(err, 'Failed to reactivate user', REFUSALS));
                }
            }
        });
    };

    const toggleUserDepartment = async (departmentId) => {
        if (!selectedUser) return;

        const isAssigned = userDepartments.some(ud => ud.department_id === departmentId);

        try {
            if (isAssigned) {
                const record = userDepartments.find(ud => ud.department_id === departmentId);
                await axios.delete(`${API_BASE_URL}/api/user_departments/${record.id}`, { withCredentials: true });
                setUserDepartments(prev => prev.filter(ud => ud.department_id !== departmentId));
            } else {
                await axios.post(
                    `${API_BASE_URL}/api/user_departments`,
                    { user_id: selectedUser.id, department_id: departmentId },
                    { withCredentials: true }
                );
            }

            // Always re-pull the authoritative state from the server after a
            // toggle so the panel reflects auto-grant of dept defaults (POST)
            // and stays consistent on DELETE. Trusting the in-memory diff alone
            // misses server-side side effects (default tool grants).
            const [deptRes, userRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/user_departments?user_id=${selectedUser.id}`, { withCredentials: true }),
                axios.get(`${API_BASE_URL}/api/users`, { withCredentials: true }),
            ]);
            setUserDepartments(deptRes.data);
            setUsers(userRes.data);
            const freshUser = userRes.data.find(u => u.id === selectedUser.id);
            if (freshUser) setSelectedUser(freshUser);
        } catch (err) {
            toastError(errorMessage(err, 'Failed to update user department', REFUSALS));
        }
    };

    const deleteUserPermanently = async () => {
        if (!selectedUser) return;

        setConfirmModal({
            open: true,
            title: 'Delete User Permanently?',
            message: `Are you sure you want to permanently delete ${selectedUser.name}? This action cannot be undone. All their assignments will be removed.`,
            confirmText: 'Delete Permanently',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/users/${selectedUser.id}`, { withCredentials: true });
                    setUsers(users.filter(u => u.id !== selectedUser.id));
                    setSelectedUser(null);
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    setConfirmModal(prev => ({ ...prev, open: false }));
                    toastError(errorMessage(err, 'Failed to delete user', REFUSALS));
                }
            }
        });
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        const email = addUserForm.email?.trim();
        if (!email) {
            setAddUserError('Email is required');
            return;
        }
        setAddUserError('');
        setAddUserSubmitting(true);
        try {
            const res = await axios.post(`${API_BASE_URL}/api/users`, { email, name: addUserForm.name?.trim() || undefined }, { withCredentials: true });
            const newUser = res.data;
            setAddUserModal({ open: false });
            setAddUserForm({ email: '', name: '' });
            // Clear any per-user state from a previously selected user so the new
            // user starts truly empty (no carried-over departments, tools, etc.)
            setUserDepartments([]);
            setUserActionItems([]);
            setUserPolicies([]);
            setAssignedActionItems([]);
            setAssignedPolicies([]);
            await fetchUsers();
            // Use handleUserSelect to refetch the new user's (empty) state from
            // the server rather than trusting in-memory props.
            await handleUserSelect(newUser);
        } catch (err) {
            setAddUserError(err.response?.data?.error || err.message || 'Failed to add user');
        } finally {
            setAddUserSubmitting(false);
        }
    };

    // Category CRUD
    const openCategoryModal = (category = null) => {
        if (category) {
            setCategoryForm({ name: category.name, icon: category.icon });
            setCategoryModal({ open: true, category });
        } else {
            setCategoryForm({ name: '', icon: '' });
            setCategoryModal({ open: true, category: null });
        }
    };

    // DEPARTMENT CRUD
    const openDepartmentModal = (department = null) => {
        if (department) {
            setDepartmentForm({ name: department.name, description: department.description || '' });
            setDepartmentModal({ open: true, department });
        } else {
            setDepartmentForm({ name: '', description: '' });
            setDepartmentModal({ open: true, department: null });
        }
    };

    const saveDepartmentForm = async () => {
        try {
            if (departmentModal.department) {
                await axios.put(`${API_BASE_URL}/api/departments/${departmentModal.department.id}`, departmentForm, { withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/departments`, departmentForm, { withCredentials: true });
            }
            setDepartmentModal({ open: false, department: null });
            fetchDepartments();
        } catch (err) {
            setConfirmModal({
                open: true,
                title: 'Error',
                message: 'Failed to save department: ' + err.message,
                onConfirm: () => setConfirmModal({ ...confirmModal, open: false }),
                confirmText: 'OK',
                isDangerous: true
            });
        }
    };

    const deleteDepartment = (id) => {
        setConfirmModal({
            open: true,
            title: 'Delete Department',
            message: 'Are you sure you want to delete this department? Users in this department will become unassigned.',
            confirmText: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/departments/${id}`, { withCredentials: true });
                    fetchDepartments();
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    // ignore
                }
            }
        });
    };

    // Stores detailed rows: [{ id, tool_id, visibility }]
    const refetchDepartmentTools = async (deptId) => {
        const res = await axios.get(`${API_BASE_URL}/api/department_tools?department_id=${deptId}&detailed=1`, { withCredentials: true });
        setDepartmentToolRows(res.data || []);
        setDepartmentTools((res.data || []).map((r) => r.tool_id));
    };

    const handleDepartmentSelect = async (dept) => {
        setSelectedDepartment(dept);
        setDeptDrawerToolFilter('');
        try {
            await refetchDepartmentTools(dept.id);
        } catch (err) {
            console.error('Failed to fetch department tools', err);
        }
    };

    const toggleDepartmentTool = async (toolId) => {
        if (!selectedDepartment) return;
        const existing = departmentToolRows.find((r) => r.tool_id === toolId);
        try {
            if (existing) {
                await axios.delete(`${API_BASE_URL}/api/department_tools/${existing.id}`, { withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/department_tools`, {
                    department_id: selectedDepartment.id,
                    tool_id: toolId,
                    visibility: 'all'
                }, { withCredentials: true });
            }
            await refetchDepartmentTools(selectedDepartment.id);
        } catch (err) {
            toastError(errorMessage(err, "Couldn't update the department's tools."));
        }
    };

    const setDepartmentToolVisibility = async (toolId, visibility) => {
        if (!selectedDepartment) return;
        const existing = departmentToolRows.find((r) => r.tool_id === toolId);
        if (!existing) return;
        try {
            await axios.patch(
                `${API_BASE_URL}/api/department_tools/${existing.id}/visibility`,
                { visibility },
                { withCredentials: true }
            );
            await refetchDepartmentTools(selectedDepartment.id);
        } catch (err) {
            console.error('Failed to update visibility', err);
        }
    };

    // CATEGORY CRUD
    const saveCategoryForm = async () => {
        try {
            if (categoryModal.category) {
                await axios.put(`${API_BASE_URL}/api/categories/${categoryModal.category.id}`, categoryForm, { withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/categories`, categoryForm, { withCredentials: true });
            }
            setCategoryModal({ open: false, category: null });
            fetchCategories();
        } catch (err) {
            setConfirmModal({
                open: true,
                title: 'Error',
                message: 'Failed to save category: ' + err.message,
                onConfirm: () => setConfirmModal({ ...confirmModal, open: false }),
                confirmText: 'OK',
                isDangerous: true
            });
        }
    };

    const deleteCategory = (id) => {
        setConfirmModal({
            open: true,
            title: 'Delete Category',
            message: 'Are you sure you want to delete this category? All tools in this category will become uncategorized.',
            confirmText: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/categories/${id}`, { withCredentials: true });
                    fetchCategories();
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    // alert('Failed to delete category: ' + err.message);
                }
            }
        });
    };

    // Tool CRUD
    const openToolModal = async (tool = null) => {
        if (tool) {
            // Fetch departments assigned to this tool
            try {
                const deptRes = await axios.get(`${API_BASE_URL}/api/tool_departments?tool_id=${tool.id}`, { withCredentials: true });
                const deptIds = deptRes.data.map(d => d.department_id);
                setToolForm({ name: tool.name, description: tool.description, icon_url: tool.icon_url, link: tool.link, category_id: tool.category_id, type: tool.type || 'tool', department_ids: deptIds });
            } catch (err) {
                setToolForm({ name: tool.name, description: tool.description, icon_url: tool.icon_url, link: tool.link, category_id: tool.category_id, type: tool.type || 'tool', department_ids: [] });
            }
            setToolModal({ open: true, tool });
        } else {
            setToolForm({ name: '', description: '', icon_url: '', link: '', category_id: '', type: 'tool', department_ids: [] });
            setToolModal({ open: true, tool: null });
        }
    };

    const saveToolForm = async () => {
        try {
            let toolId;
            if (toolModal.tool) {
                await axios.put(`${API_BASE_URL}/api/tools/${toolModal.tool.id}`, toolForm, { withCredentials: true });
                toolId = toolModal.tool.id;
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/tools`, toolForm, { withCredentials: true });
                toolId = res.data.id;
            }
            
            // Update department assignments — only when the caller can manage
            // department tool grants. A tools.manage-only save must not touch
            // /api/department_tools at all (the server would 403 anyway).
            if (can('departments.manage')) {
                if (toolForm.department_ids && toolForm.department_ids.length > 0) {
                    // Get current department assignments
                    const currentDepts = await axios.get(`${API_BASE_URL}/api/tool_departments?tool_id=${toolId}`, { withCredentials: true });
                    const currentDeptIds = currentDepts.data.map(d => d.department_id);

                    // Add new departments
                    for (const deptId of toolForm.department_ids) {
                        if (!currentDeptIds.includes(deptId)) {
                            await axios.post(`${API_BASE_URL}/api/department_tools`, { department_id: deptId, tool_id: toolId }, { withCredentials: true });
                        }
                    }

                    // Remove unselected departments
                    for (const currentDept of currentDepts.data) {
                        if (!toolForm.department_ids.includes(currentDept.department_id)) {
                            await axios.delete(`${API_BASE_URL}/api/department_tools/${currentDept.id}`, { withCredentials: true });
                        }
                    }
                } else if (toolModal.tool) {
                    // Remove all department assignments if none selected
                    const currentDepts = await axios.get(`${API_BASE_URL}/api/tool_departments?tool_id=${toolId}`, { withCredentials: true });
                    for (const dept of currentDepts.data) {
                        await axios.delete(`${API_BASE_URL}/api/department_tools/${dept.id}`, { withCredentials: true });
                    }
                }
            }

            setToolModal({ open: false, tool: null });
            fetchTools();
        } catch (err) {
            const serverMsg = err?.response?.data?.error;
            setConfirmModal({
                open: true,
                title: 'Error',
                message: serverMsg
                    ? `Failed to save tool: ${serverMsg}`
                    : `Failed to save tool: ${err.message || 'Unknown error'}`,
                onConfirm: () => setConfirmModal({ ...confirmModal, open: false }),
                confirmText: 'OK',
                isDangerous: true
            });
        }
    };

    const deleteTool = (id) => {
        setConfirmModal({
            open: true,
            title: 'Delete Tool',
            message: 'Are you sure you want to delete this tool?',
            confirmText: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/tools/${id}`, { withCredentials: true });
                    fetchTools();
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    // alert('Failed to delete tool: ' + err.message);
                }
            }
        });
    };

    // Action item CRUD
    const openActionModal = (action = null) => {
        if (action) {
            setActionForm({ title: action.title, description: action.description || '' });
            setActionModal({ open: true, action });
        } else {
            setActionForm({ title: '', description: '' });
            setActionModal({ open: true, action: null });
        }
    };

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
        ]
    };

    const quillFormats = [
        'header', 'bold', 'italic', 'underline',
        'list', 'bullet', 'link'
    ];

    const saveActionForm = async () => {
        const payload = {
            title: (actionForm.title && String(actionForm.title).trim()) || '',
            description: actionForm.description != null ? actionForm.description : ''
        };
        if (!payload.title) {
            setConfirmModal({
                open: true,
                title: 'Validation',
                message: 'Title is required.',
                onConfirm: () => setConfirmModal((c) => ({ ...c, open: false })),
                confirmText: 'OK',
                isDangerous: false
            });
            return;
        }
        try {
            if (actionModal.action) {
                await axios.put(`${API_BASE_URL}/api/action_items/${actionModal.action.id}`, payload, { withCredentials: true });
                setActionItems(prev => prev.map(a => a.id === actionModal.action.id ? { ...a, ...payload } : a));
                setAssignedActionItems(prev => prev.map(a => a.id === actionModal.action.id ? { ...a, ...payload } : a));
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/action_items`, payload, { withCredentials: true });
                if (selectedUser && res.data?.id) {
                    await assignActionToUser(res.data.id);
                    const newAction = { id: res.data.id, ...payload, display_order: actionItems.length };
                    setActionItems(prev => [...prev, newAction]);
                    setAssignedActionItems(prev => [...prev, newAction]);
                } else if (res.data?.id) {
                    setActionItems(prev => [...prev, { id: res.data.id, ...payload, display_order: actionItems.length }]);
                }
            }
            setActionModal({ open: false, action: null });
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || err.message;
            setConfirmModal({
                open: true,
                title: 'Error',
                message: msg ? `Failed to save action item: ${msg}` : 'Failed to save action item. Please try again.',
                onConfirm: () => setConfirmModal((c) => ({ ...c, open: false })),
                confirmText: 'OK',
                isDangerous: true
            });
        }
    };

    const deleteActionItem = (id) => {
        setConfirmModal({
            open: true,
            title: 'Delete Action Item',
            message: 'Are you sure you want to delete this action item?',
            confirmText: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/action_items/${id}`, { withCredentials: true });
                    setUserActionItems(prev => prev.filter(actionId => actionId !== id));
                    setAssignedActionItems(prev => prev.filter(action => action.id !== id));
                    setActionItems(prev => prev.filter(action => action.id !== id));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    // ignore
                }
            }
        });
    };

    // Policy CRUD
    const openPolicyModal = (policy = null) => {
        if (policy) {
            setPolicyForm({ name: policy.name, link: policy.link || '' });
            setPolicyModal({ open: true, policy });
        } else {
            setPolicyForm({ name: '', link: '' });
            setPolicyModal({ open: true, policy: null });
        }
    };

    const savePolicyForm = async () => {
        const payload = {
            name: (policyForm.name && String(policyForm.name).trim()) || '',
            link: (policyForm.link && String(policyForm.link).trim()) || ''
        };
        if (!payload.name) {
            setConfirmModal({
                open: true,
                title: 'Validation',
                message: 'Name is required.',
                onConfirm: () => setConfirmModal((c) => ({ ...c, open: false })),
                confirmText: 'OK',
                isDangerous: false
            });
            return;
        }
        if (!payload.link) {
            setConfirmModal({
                open: true,
                title: 'Validation',
                message: 'Link is required.',
                onConfirm: () => setConfirmModal((c) => ({ ...c, open: false })),
                confirmText: 'OK',
                isDangerous: false
            });
            return;
        }
        try {
            if (policyModal.policy) {
                await axios.put(`${API_BASE_URL}/api/policies/${policyModal.policy.id}`, payload, { withCredentials: true });
                setPolicies(prev => prev.map(p => p.id === policyModal.policy.id ? { ...p, ...payload } : p));
                setAssignedPolicies(prev => prev.map(p => p.id === policyModal.policy.id ? { ...p, ...payload } : p));
            } else {
                const res = await axios.post(`${API_BASE_URL}/api/policies`, payload, { withCredentials: true });
                if (selectedUser && res.data?.id) {
                    await assignPolicyToUser(res.data.id);
                    const newPolicy = { id: res.data.id, ...payload, display_order: policies.length };
                    setPolicies(prev => [...prev, newPolicy]);
                    setAssignedPolicies(prev => [...prev, newPolicy]);
                } else if (res.data?.id) {
                    setPolicies(prev => [...prev, { id: res.data.id, ...payload, display_order: policies.length }]);
                }
            }
            setPolicyModal({ open: false, policy: null });
        } catch (err) {
            const msg = err.response?.data?.error || err.response?.data?.message || err.message;
            setConfirmModal({
                open: true,
                title: 'Error',
                message: msg ? `Failed to save policy: ${msg}` : 'Failed to save policy. Please try again.',
                onConfirm: () => setConfirmModal((c) => ({ ...c, open: false })),
                confirmText: 'OK',
                isDangerous: true
            });
        }
    };

    const deletePolicy = (id) => {
        setConfirmModal({
            open: true,
            title: 'Delete Policy',
            message: 'Are you sure you want to delete this policy?',
            confirmText: 'Delete',
            isDangerous: true,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API_BASE_URL}/api/policies/${id}`, { withCredentials: true });
                    setUserPolicies(prev => prev.filter(policyId => policyId !== id));
                    setAssignedPolicies(prev => prev.filter(policy => policy.id !== id));
                    setPolicies(prev => prev.filter(policy => policy.id !== id));
                    setConfirmModal(prev => ({ ...prev, open: false }));
                } catch (err) {
                    // ignore
                }
            }
        });
    };

    // Handle drag and drop for action items
    const handleActionItemDragEnd = async (event) => {
        const { active, over } = event;
        
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = assignedActionItems.findIndex(item => item.id === active.id);
        const newIndex = assignedActionItems.findIndex(item => item.id === over.id);
        
        const items = arrayMove(assignedActionItems, oldIndex, newIndex);

        // Optimistically update local state only
        setAssignedActionItems(items);

        // Send update to backend
        try {
            await axios.post(`${API_BASE_URL}/api/action_items/reorder`, {
                items: items.map((item, index) => ({ id: item.id, display_order: index }))
            }, { withCredentials: true });
        } catch (err) {
            console.error('Failed to reorder action items', err);
            // Revert on error
            const assigned = actionItems.filter(action => userActionItems.includes(action.id)).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            setAssignedActionItems(assigned);
        }
    };

    // Handle drag and drop for policies
    const handlePolicyDragEnd = async (event) => {
        const { active, over } = event;
        
        if (!over || active.id === over.id) {
            return;
        }

        const oldIndex = assignedPolicies.findIndex(item => item.id === active.id);
        const newIndex = assignedPolicies.findIndex(item => item.id === over.id);
        
        const items = arrayMove(assignedPolicies, oldIndex, newIndex);

        // Optimistically update local state only
        setAssignedPolicies(items);

        // Send update to backend
        try {
            await axios.post(`${API_BASE_URL}/api/policies/reorder`, {
                items: items.map((item, index) => ({ id: item.id, display_order: index }))
            }, { withCredentials: true });
        } catch (err) {
            console.error('Failed to reorder policies', err);
            // Revert on error
            const assigned = policies.filter(policy => userPolicies.includes(policy.id)).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
            setAssignedPolicies(assigned);
        }
    };

    // Group tools by category for User Permissions tab
    const toolsByCategory = useMemo(() => {
        return tools.reduce((acc, tool) => {
            const catName = tool.category || 'Uncategorized';
            if (!acc[catName]) acc[catName] = [];
            acc[catName].push(tool);
            return acc;
        }, {});
    }, [tools]);

    // Configure drag sensors for @dnd-kit
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Require 8px of movement before drag starts (prevents accidental drags)
            },
        })
    );

    // Sortable Action Item Component
    const SortableActionItem = ({ action }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: action.id });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: isDragging ? '#f7fafc' : 'white',
            boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            opacity: isDragging ? 0.5 : 1,
        };

        // Description is rich-text HTML — strip to a single-line preview for
        // the card so the editor's <p>/<ul> markup doesn't break the layout.
        const previewText = (action.description || '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        return (
            <div ref={setNodeRef} style={style}>
                <div {...(isFullAdmin ? { ...attributes, ...listeners } : {})} style={{ color: '#a0aec0', fontSize: '16px', cursor: isFullAdmin ? 'grab' : 'default', padding: '4px 2px', userSelect: 'none', WebkitUserDrag: 'none', touchAction: 'none', flexShrink: 0 }}>⋮⋮</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{action.title}</div>
                    {previewText && (
                        <div title={previewText} style={{ fontSize: '12px', color: '#718096', marginTop: '4px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {previewText}
                        </div>
                    )}
                </div>
                {isFullAdmin && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button className="btn btn-small btn-secondary" type="button" onClick={(e) => { e.stopPropagation(); openActionModal(action); }}>Edit</button>
                        <button className="btn btn-small btn-danger" type="button" onClick={(e) => { e.stopPropagation(); deleteActionItem(action.id); }}>Remove</button>
                    </div>
                )}
            </div>
        );
    };

    // Sortable Policy Component
    const SortablePolicy = ({ policy }) => {
        const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: policy.id });

        const style = {
            transform: CSS.Transform.toString(transform),
            transition,
            display: 'flex',
            alignItems: 'flex-start',
            gap: '10px',
            padding: '12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: isDragging ? '#f7fafc' : 'white',
            boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : 'none',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            opacity: isDragging ? 0.5 : 1,
        };

        return (
            <div ref={setNodeRef} style={style}>
                <div {...(isFullAdmin ? { ...attributes, ...listeners } : {})} style={{ color: '#a0aec0', fontSize: '16px', cursor: isFullAdmin ? 'grab' : 'default', padding: '4px 2px', userSelect: 'none', WebkitUserDrag: 'none', touchAction: 'none', flexShrink: 0 }}>⋮⋮</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#2d3748', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{policy.name}</div>
                    {policy.link && (
                        <a
                            href={policy.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={policy.link}
                            onClick={(e) => e.stopPropagation()}
                            style={{ fontSize: '11px', color: '#3182ce', marginTop: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: 'none', display: 'block' }}
                        >
                            {policy.link}
                        </a>
                    )}
                </div>
                {isFullAdmin && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <button className="btn btn-small btn-secondary" type="button" onClick={(e) => { e.stopPropagation(); openPolicyModal(policy); }}>Edit</button>
                        <button className="btn btn-small btn-danger" type="button" onClick={(e) => { e.stopPropagation(); deletePolicy(policy.id); }}>Remove</button>
                    </div>
                )}
            </div>
        );
    };

    const tabMeta = TAB_META[activeTab] || {};

    return (
        <AdminShell
            activeKey={activeTab}
            counts={{ tools: pendingRequestsCount, apps: pendingAppCount }}
            title={tabMeta.title}
            subtitle={tabMeta.subtitle}
            actions={activeTab === 'apps' ? <SsmStatusPill /> : null}
        >
            <div className="admin-content" data-active-tab={activeTab}>
              {tabAllowed ? (
                <>

                {activeTab === 'overview' && <AdminOverview />}

                {activeTab === 'sync' && <OdooSyncTab />}

                {activeTab === 'vendors' && <AdminVendorsTab />}

                {activeTab === 'it_apps' && <ItAppsTab />}

                {activeTab === 'settings' && <AdminSettings />}

                {activeTab === 'roles' && <RolesPage />}

                {activeTab === 'apps' && (
                    <>
                        {!buildAppEnabled && (
                          <div className="admin-apps-disabled-banner" role="status">
                            Build your app is currently <strong>disabled</strong> for users. Existing apps are still manageable here; no new requests can be created until it&apos;s re-enabled in Settings.
                          </div>
                        )}
                        <AppsPipeline />
                    </>
                )}

                {/* User Permissions Tab: left column fixed, only right panel scrolls */}
                {activeTab === 'users' && (
                    <AdminUsersTab
                        users={users}
                        departments={departments}
                        selectedUser={selectedUser}
                        onSelect={(u) => { if (u) handleUserSelect(u); else setSelectedUser(null); }}
                        onAddUserClick={can('users.edit') ? () => { setAddUserModal({ open: true }); setAddUserError(''); setAddUserForm({ email: '', name: '' }); } : undefined}
                    >
                        {selectedUser && (
                            <>
                                <UserDetailHeader
                                    user={selectedUser}
                                    onToggleAdmin={isFullAdmin ? toggleAdminStatus : undefined}
                                    onDeactivate={can('users.deactivate') && !selectedUser.is_admin ? deactivateUser : undefined}
                                    onReactivate={can('users.deactivate') && !selectedUser.is_admin ? reactivateUser : undefined}
                                    onDeletePermanently={can('users.deactivate') && !selectedUser.is_admin ? deleteUserPermanently : undefined}
                                    onSyncOdoo={can('sync.user') ? syncUserFromOdoo : undefined}
                                    syncing={syncingUser}
                                    onRolesChanged={isFullAdmin && rbacOn ? async () => {
                                        const userRes = await axios.get(`${API_BASE_URL}/api/users`, { withCredentials: true });
                                        setUsers(userRes.data);
                                        const fresh = userRes.data.find((u) => u.id === selectedUser.id);
                                        if (fresh) setSelectedUser(fresh);
                                    } : undefined}
                                />
                                <UserDepartmentsSection
                                    user={selectedUser}
                                    userDepartments={userDepartments}
                                    departments={departments}
                                    onToggleDepartment={toggleUserDepartment}
                                    readOnly={!can('departments.members')}
                                />
                                <div style={{ padding: '20px' }}>
                                            <UserPermissionsToolsPanel
                                                user={selectedUser}
                                                getImageUrl={getImageUrl}
                                                readOnly={!can('users.grants')}
                                            />

                                <div style={{ borderTop: '2px solid #e2e8f0', marginTop: '40px', marginBottom: '30px' }} />

                                {can('users.grants') && (
                                <div style={{ marginBottom: '30px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                        <h3 style={{ fontSize: '16px', margin: 0, color: '#2d3748', fontWeight: 600 }}>
                                            Action List
                                            {isFullAdmin && (
                                                <span style={{ fontSize: '11px', color: '#718096', fontWeight: 400, marginLeft: '8px' }}>(Drag to reorder)</span>
                                            )}
                                        </h3>
                                        {isFullAdmin && (
                                            <button className="btn btn-small btn-primary" onClick={() => openActionModal()}>Add Action Item</button>
                                        )}
                                    </div>
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleActionItemDragEnd}>
                                        <SortableContext items={assignedActionItems.map(item => item.id)} strategy={verticalListSortingStrategy}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {assignedActionItems.map((action) => (
                                                    <SortableActionItem key={action.id} action={action} />
                                                ))}
                                                {assignedActionItems.length === 0 && (
                                                    <div style={{ color: '#a0aec0', fontStyle: 'italic' }}>No action items assigned</div>
                                                )}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                                )}

                                {can('users.grants') && (
                                <div style={{ marginBottom: '30px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #edf2f7', paddingBottom: '8px' }}>
                                        <h3 style={{ fontSize: '16px', margin: 0, color: '#2d3748', fontWeight: 600 }}>
                                            Policies
                                            {isFullAdmin && (
                                                <span style={{ fontSize: '11px', color: '#718096', fontWeight: 400, marginLeft: '8px' }}>(Drag to reorder)</span>
                                            )}
                                        </h3>
                                        {isFullAdmin && (
                                            <button className="btn btn-small btn-primary" onClick={() => openPolicyModal()}>Add Policy</button>
                                        )}
                                    </div>
                                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePolicyDragEnd}>
                                        <SortableContext items={assignedPolicies.map(item => item.id)} strategy={verticalListSortingStrategy}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {assignedPolicies.map((policy) => (
                                                    <SortablePolicy key={policy.id} policy={policy} />
                                                ))}
                                                {assignedPolicies.length === 0 && (
                                                    <div style={{ color: '#a0aec0', fontStyle: 'italic' }}>No policies assigned</div>
                                                )}
                                            </div>
                                        </SortableContext>
                                    </DndContext>
                                </div>
                                )}
                            </div>
                            </>
                        )}
                    </AdminUsersTab>
                )}

                {/* Add User Modal */}
                {addUserModal.open && (
                    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }} onClick={() => !addUserSubmitting && setAddUserModal({ open: false })}>
                        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }} onClick={(e) => e.stopPropagation()}>
                            <h2 style={{ margin: '0 0 16px', fontSize: '20px' }}>Add user</h2>
                            <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#718096' }}>
                                Add a user by email. They will sign in with Google and see the departments and tools you assign.
                            </p>
                            <form onSubmit={handleAddUser}>
                                <div className="form-group" style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Email *</label>
                                    <input
                                        type="email"
                                        value={addUserForm.email}
                                        onChange={(e) => setAddUserForm({ ...addUserForm, email: e.target.value })}
                                        placeholder="e.g. john@company.com"
                                        required
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                        autoFocus
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>Name (optional)</label>
                                    <input
                                        type="text"
                                        value={addUserForm.name}
                                        onChange={(e) => setAddUserForm({ ...addUserForm, name: e.target.value })}
                                        placeholder="Display name until they sign in"
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                    />
                                </div>
                                {addUserError && <p style={{ color: '#c53030', fontSize: '13px', marginBottom: '12px' }}>{addUserError}</p>}
                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                    <button type="button" className="btn btn-secondary" onClick={() => setAddUserModal({ open: false })} disabled={addUserSubmitting}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={addUserSubmitting}>{addUserSubmitting ? 'Adding...' : 'Add user'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Manage Departments Tab */}
                {activeTab === 'departments' && (
                    <AdminDepartmentsTab
                        departments={departmentsRich.length > 0 ? departmentsRich : departments}
                        selectedDepartment={selectedDepartment}
                        onSelect={(d) => { if (d) handleDepartmentSelect(d); else setSelectedDepartment(null); }}
                        onAddDepartment={can('departments.manage') ? () => openDepartmentModal() : undefined}
                    >
                        {selectedDepartment && (
                            <div style={{ padding: '20px' }}>
                                <>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                        <h2 style={{ margin: 0 }}>Default Tools for: <span style={{ color: '#E9453E' }}>{selectedDepartment.name}</span></h2>
                                        {can('departments.manage') && (
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button className="btn btn-small btn-secondary" onClick={() => openDepartmentModal(selectedDepartment)}>
                                                    Edit Department
                                                </button>
                                                <button className="btn btn-small btn-danger" onClick={() => deleteDepartment(selectedDepartment.id)}>
                                                    Delete Department
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <p style={{ marginBottom: '16px', color: '#718096' }}>
                                        Select which tools should be assigned by default to all users in this department. Users can still have individual customizations.
                                    </p>

                                    <div style={{ position: 'relative', marginBottom: '20px' }}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"
                                             style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#a0aec0', pointerEvents: 'none' }}>
                                            <circle cx="11" cy="11" r="7" />
                                            <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
                                        </svg>
                                        <input
                                            type="search"
                                            placeholder="Search tools by name or category…"
                                            value={deptDrawerToolFilter}
                                            onChange={(e) => setDeptDrawerToolFilter(e.target.value)}
                                            aria-label="Filter department tools"
                                            style={{ width: '100%', height: 40, padding: '0 36px 0 36px', border: '1px solid #e2e8f0', borderRadius: 10, font: 'inherit', fontSize: 14 }}
                                        />
                                        {deptDrawerToolFilter && (
                                            <button type="button" onClick={() => setDeptDrawerToolFilter('')} aria-label="Clear search"
                                                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'transparent', fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#718096' }}>
                                                ×
                                            </button>
                                        )}
                                    </div>

                                    {(() => {
                                        const q = deptDrawerToolFilter.trim().toLowerCase();
                                        const allCategories = Object.entries(toolsByCategory).map(([categoryName, categoryTools]) => {
                                            const filtered = q
                                                ? categoryTools.filter((t) =>
                                                    (t.name || '').toLowerCase().includes(q) ||
                                                    categoryName.toLowerCase().includes(q)
                                                  )
                                                : categoryTools;
                                            return [categoryName, filtered];
                                        }).filter(([, items]) => items.length > 0);
                                        if (allCategories.length === 0) {
                                            return (
                                                <div style={{ textAlign: 'center', padding: 32, color: '#a0aec0', fontStyle: 'italic' }}>
                                                    No tools match "{deptDrawerToolFilter}".
                                                </div>
                                            );
                                        }
                                        return allCategories.map(([categoryName, categoryTools]) => {
                                        const enabledCount = categoryTools.filter(t => departmentTools.includes(t.id)).length;
                                        const totalCount = categoryTools.length;
                                        const allSelected = enabledCount === totalCount;

                                        const toggleCategory = () => {
                                            if (allSelected) {
                                                categoryTools.forEach(tool => {
                                                    if (departmentTools.includes(tool.id)) toggleDepartmentTool(tool.id);
                                                });
                                            } else {
                                                categoryTools.forEach(tool => {
                                                    if (!departmentTools.includes(tool.id)) toggleDepartmentTool(tool.id);
                                                });
                                            }
                                        };

                                        return (
                                            <div key={categoryName} style={{ marginBottom: '30px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #edf2f7', paddingBottom: '8px' }}>
                                                    <h3 style={{ fontSize: '16px', margin: 0, color: '#2d3748', fontWeight: 600 }}>
                                                        {categoryName} <span style={{ fontSize: '13px', color: '#718096', fontWeight: 400, marginLeft: '8px' }}>({enabledCount}/{totalCount} enabled)</span>
                                                    </h3>
                                                    {can('departments.manage') && (
                                                        <button className="btn btn-small btn-secondary" onClick={toggleCategory}>
                                                            {allSelected ? 'Deselect All' : 'Select All'}
                                                        </button>
                                                    )}
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                                                    {categoryTools.map(tool => {
                                                        const row = departmentToolRows.find((r) => r.tool_id === tool.id);
                                                        const isAssigned = !!row;
                                                        const isManagersOnly = row?.visibility === 'managers_only';
                                                        return (
                                                            <div
                                                                key={tool.id}
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: '8px',
                                                                    padding: '12px',
                                                                    border: isAssigned ? '2px solid #E9453E' : '1px solid #e2e8f0',
                                                                    borderRadius: '8px',
                                                                    background: isAssigned ? 'rgba(233, 69, 62, 0.04)' : 'white'
                                                                }}
                                                            >
                                                                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isAssigned}
                                                                        disabled={!can('departments.manage')}
                                                                        onChange={() => toggleDepartmentTool(tool.id)}
                                                                        style={{ width: '18px', height: '18px', cursor: can('departments.manage') ? 'pointer' : 'not-allowed', accentColor: '#E9453E' }}
                                                                    />
                                                                    <div style={{
                                                                        width: '36px', height: '36px', borderRadius: '6px',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        flexShrink: 0, overflow: 'hidden', background: '#f7fafc'
                                                                    }}>
                                                                        {tool.icon_url && (
                                                                            <img src={getImageUrl(tool.icon_url)} alt={tool.name} style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                                                                        )}
                                                                    </div>
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#2d3748', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tool.name}</div>
                                                                        <div style={{ fontSize: '11px', color: '#718096', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>{tool.description}</div>
                                                                    </div>
                                                                </label>
                                                                {isAssigned && can('departments.manage') && (
                                                                    <div role="group" aria-label={`Visibility for ${tool.name}`} style={{ display: 'flex', gap: '6px', paddingLeft: '30px' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setDepartmentToolVisibility(tool.id, 'all')}
                                                                            aria-pressed={!isManagersOnly}
                                                                            title="Visible to every member of the department, auto-granted on join."
                                                                            style={{
                                                                                flex: 1,
                                                                                padding: '6px 8px',
                                                                                fontSize: '11px',
                                                                                fontWeight: 600,
                                                                                borderRadius: '6px',
                                                                                cursor: 'pointer',
                                                                                border: !isManagersOnly ? '1px solid #0F1E3C' : '1px solid #e2e8f0',
                                                                                background: !isManagersOnly ? '#0F1E3C' : 'white',
                                                                                color: !isManagersOnly ? 'white' : '#4a5568'
                                                                            }}
                                                                        >
                                                                            All members
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setDepartmentToolVisibility(tool.id, 'managers_only')}
                                                                            aria-pressed={isManagersOnly}
                                                                            title="Hidden from employees and not auto-granted. Managers see it on their department page."
                                                                            style={{
                                                                                flex: 1,
                                                                                padding: '6px 8px',
                                                                                fontSize: '11px',
                                                                                fontWeight: 600,
                                                                                borderRadius: '6px',
                                                                                cursor: 'pointer',
                                                                                border: isManagersOnly ? '1px solid #92400E' : '1px solid #e2e8f0',
                                                                                background: isManagersOnly ? '#FEF3C7' : 'white',
                                                                                color: isManagersOnly ? '#92400E' : '#4a5568'
                                                                            }}
                                                                        >
                                                                            Managers only
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                        });
                                    })()}
                                </>
                            </div>
                        )}
                    </AdminDepartmentsTab>
                )}

                {/* Manage Categories Tab */}
                {activeTab === 'categories' && (
                    <div>
                        <div className="filter-bar">
                            <input
                                type="text"
                                placeholder="Search categories..."
                                className="search-input"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {can('categories.manage') && (
                                <button className="btn btn-primary" onClick={() => openCategoryModal()}>Add Category</button>
                            )}
                        </div>

                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '72px' }}>ID</th>
                                        <th>Name</th>
                                        <th style={{ width: '80px' }}>Icon</th>
                                        <th style={{ width: '170px', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCategories.map(cat => (
                                        <tr key={cat.id}>
                                            <td className="at-id">#{cat.id}</td>
                                            <td className="at-primary">{cat.name}</td>
                                            <td>
                                                {cat.icon && cat.icon.trim().startsWith('<') ? (
                                                    <div dangerouslySetInnerHTML={{ __html: cat.icon }} style={{ width: '24px', height: '24px' }} />
                                                ) : (
                                                    <span style={{ fontSize: '20px' }}>{cat.icon}</span>
                                                )}
                                            </td>
                                            <td>
                                                {can('categories.manage') && (
                                                    <div className="at-actions">
                                                        <button className="btn btn-small btn-secondary" onClick={() => openCategoryModal(cat)}>Edit</button>
                                                        <button className="btn btn-small btn-danger-outline" onClick={() => deleteCategory(cat.id)}>Delete</button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredCategories.length === 0 && (
                                        <tr><td colSpan="4" className="at-empty">No categories found matching "{searchQuery}"</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Manage Tools Tab */}
                {activeTab === 'tools' && (
                    <div>
                        <nav className="admin-subtabs" aria-label="Tools sub-navigation">
                            {can('tools.manage') && (
                                <button
                                    type="button"
                                    className={`admin-subtab ${toolsSubTab === 'catalog' ? 'is-active' : ''}`}
                                    onClick={() => setToolsSubTab('catalog')}
                                >
                                    Catalog
                                </button>
                            )}
                            {canAny(['tool_access.resolve', 'tool_proposals.publish']) && (
                                <button
                                    type="button"
                                    className={`admin-subtab ${toolsSubTab === 'requests' ? 'is-active' : ''}`}
                                    onClick={() => setToolsSubTab('requests')}
                                >
                                    Requests
                                    {pendingRequestsCount > 0 && (
                                        <span className="admin-subtab-badge">{pendingRequestsCount}</span>
                                    )}
                                </button>
                            )}
                        </nav>
                        {toolsSubTab === 'catalog' && can('tools.manage') && (
                        <div className="tools-catalog-shell">
                            <aside className="tools-catalog-rail" aria-label="Catalog filters">
                                <div className="tools-rail-group">
                                    <div className="tools-rail-head">Categories</div>
                                    <ul className="tools-rail-list">
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${categoryFilter === 'All' ? 'is-active' : ''}`}
                                                onClick={() => setCategoryFilter('All')}
                                            >
                                                <span>All</span>
                                                <span className="tools-rail-count">{toolFacetCounts.byCategory.All || 0}</span>
                                            </button>
                                        </li>
                                        {categories.map(cat => (
                                            <li key={cat.id}>
                                                <button
                                                    type="button"
                                                    className={`tools-rail-row ${categoryFilter === cat.name ? 'is-active' : ''}`}
                                                    onClick={() => setCategoryFilter(cat.name)}
                                                >
                                                    <span>{cat.name}</span>
                                                    <span className="tools-rail-count">{toolFacetCounts.byCategory[cat.name] || 0}</span>
                                                </button>
                                            </li>
                                        ))}
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${categoryFilter === 'Uncategorized' ? 'is-active' : ''}`}
                                                onClick={() => setCategoryFilter('Uncategorized')}
                                            >
                                                <span>Uncategorized</span>
                                                <span className="tools-rail-count">{toolFacetCounts.byCategory.Uncategorized || 0}</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                <div className="tools-rail-group">
                                    <div className="tools-rail-head">Type</div>
                                    <ul className="tools-rail-list">
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${typeFilter === 'All' ? 'is-active' : ''}`}
                                                onClick={() => setTypeFilter('All')}
                                            >
                                                <span>All</span>
                                                <span className="tools-rail-count">{toolFacetCounts.byType.All || 0}</span>
                                            </button>
                                        </li>
                                        {TOOL_TYPES.map(({ value, label }) => (
                                            <li key={value}>
                                                <button
                                                    type="button"
                                                    className={`tools-rail-row ${typeFilter === value ? 'is-active' : ''}`}
                                                    onClick={() => setTypeFilter(value)}
                                                >
                                                    <span>{label}</span>
                                                    <span className="tools-rail-count">{toolFacetCounts.byType[value] || 0}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="tools-rail-group">
                                    <div className="tools-rail-head">Source</div>
                                    <ul className="tools-rail-list">
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${sourceFilter === 'All' ? 'is-active' : ''}`}
                                                onClick={() => setSourceFilter('All')}
                                            >
                                                <span>All</span>
                                                <span className="tools-rail-count">{toolFacetCounts.bySource.All || 0}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${sourceFilter === 'self_built' ? 'is-active' : ''}`}
                                                onClick={() => setSourceFilter('self_built')}
                                            >
                                                <span>Built in-house</span>
                                                <span className="tools-rail-count">{toolFacetCounts.bySource.self_built || 0}</span>
                                            </button>
                                        </li>
                                        <li>
                                            <button
                                                type="button"
                                                className={`tools-rail-row ${sourceFilter === 'native' ? 'is-active' : ''}`}
                                                onClick={() => setSourceFilter('native')}
                                            >
                                                <span>Catalog</span>
                                                <span className="tools-rail-count">{toolFacetCounts.bySource.native || 0}</span>
                                            </button>
                                        </li>
                                    </ul>
                                </div>

                                {(categoryFilter !== 'All' || typeFilter !== 'All' || sourceFilter !== 'All') && (
                                    <button
                                        type="button"
                                        className="tools-rail-clear"
                                        onClick={() => { setCategoryFilter('All'); setTypeFilter('All'); setSourceFilter('All'); }}
                                    >
                                        Clear filters
                                    </button>
                                )}
                            </aside>

                            <div className="tools-catalog-main">
                                <div className="filter-bar">
                                    <div style={{ position: 'relative', flex: 1 }}>
                                        <input
                                            type="text"
                                            placeholder="Search tools by name or description..."
                                            className="search-input"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    {can('tools.manage') && (
                                        <button className="btn btn-primary" onClick={() => openToolModal()}>Add Tool</button>
                                    )}
                                </div>

                                <div className="tool-grid">
                                    {filteredTools.map(tool => (
                                        <div key={tool.id} className="tool-card">
                                            <div className="tool-card-header">
                                                <div className="tool-card-icon">
                                                    {tool.icon_url && (
                                                        <img src={getImageUrl(tool.icon_url)} alt={tool.name} style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                                                    )}
                                                </div>
                                                <div className="tool-card-info">
                                                    <h4 className="tool-card-title">{tool.name}</h4>
                                                    <div className="tool-card-meta">
                                                        <span className="tool-card-category">{tool.category || 'Uncategorized'}</span>
                                                        <span className={`tool-type-badge tool-type-badge--${tool.type || 'tool'}`}>{TOOL_TYPE_LABEL[tool.type] || 'Tool'}</span>
                                                        {tool.self_built && (
                                                            <span className="tool-selfbuilt-badge" title="Published from a Build-your-app request">Built in-house</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="tool-card-desc">{tool.description}</p>
                                            <div className="tool-card-actions">
                                                <a href={tool.link} target="_blank" rel="noopener noreferrer" className="action-btn link-btn">
                                                    Open
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
                                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                        <polyline points="15 3 21 3 21 9"></polyline>
                                                        <line x1="10" y1="14" x2="21" y2="3"></line>
                                                    </svg>
                                                </a>
                                                {can('tools.manage') && (
                                                    <>
                                                        <button className="action-btn edit-btn" onClick={() => openToolModal(tool)}>Edit</button>
                                                        <button className="action-btn delete-btn" onClick={() => deleteTool(tool.id)}>Delete</button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {filteredTools.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '60px', color: '#718096', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <p style={{ fontSize: '18px', fontWeight: 500 }}>No tools found</p>
                                        <p>Try adjusting your search or filters</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                )}

                {/* Activity Logs Tab */}
                {activeTab === 'activity' && (
                    <div>
                        {/* The topbar already titles the page "Activity Logs"; no second heading here. */}
                        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <button className="btn btn-secondary" onClick={fetchActivityLogs}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
                                    <polyline points="23 4 23 10 17 10"></polyline>
                                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                                Refresh
                            </button>
                        </div>
                        
                        {/* Filters */}
                        <div style={{ marginBottom: '20px', padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end' }}>
                            <div style={{ flex: '2', minWidth: '240px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4a5568' }}>Search</label>
                                <input
                                    type="text"
                                    value={logFilters.query}
                                    onChange={(e) => setLogFilters({ ...logFilters, query: e.target.value })}
                                    placeholder="User, entity, details, action…"
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                />
                            </div>
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4a5568' }}>Filter by Action</label>
                                <select
                                    value={logFilters.action}
                                    onChange={(e) => setLogFilters({ ...logFilters, action: e.target.value })}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                >
                                    <option value="all">All Actions</option>
                                    {uniqueActions.map(action => (
                                        <option key={action} value={action}>{action.replace(/_/g, ' ').toUpperCase()}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div style={{ flex: '1', minWidth: '200px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4a5568' }}>Filter by User</label>
                                <select
                                    value={logFilters.user}
                                    onChange={(e) => setLogFilters({ ...logFilters, user: e.target.value })}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                >
                                    <option value="all">All Users</option>
                                    {uniqueLogUsers.map(user => (
                                        <option key={user.id} value={user.id}>{user.name} ({user.email})</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div style={{ flex: '0.8', minWidth: '150px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4a5568' }}>From Date</label>
                                <input
                                    type="date"
                                    value={logFilters.dateFrom}
                                    onChange={(e) => setLogFilters({ ...logFilters, dateFrom: e.target.value })}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                />
                            </div>
                            
                            <div style={{ flex: '0.8', minWidth: '150px' }}>
                                <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4a5568' }}>To Date</label>
                                <input
                                    type="date"
                                    value={logFilters.dateTo}
                                    onChange={(e) => setLogFilters({ ...logFilters, dateTo: e.target.value })}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                                />
                            </div>
                            
                            <button
                                className="btn btn-secondary"
                                onClick={() => setLogFilters({ action: 'all', user: 'all', dateFrom: '', dateTo: '', query: '' })}
                                style={{ padding: '8px 16px', height: '38px' }}
                            >
                                Clear Filters
                            </button>
                        </div>
                        
                        {/* Results count */}
                        <div className="admin-table-count">
                            Showing {filteredActivityLogs.length} of {activityLogs.length} log{activityLogs.length !== 1 ? 's' : ''}
                        </div>

                        {filteredActivityLogs.length > 0 ? (
                            <>
                            <div className="admin-table-wrap ut-tablewrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '160px' }}>Time</th>
                                            <th>User</th>
                                            <th style={{ width: '180px' }}>Action</th>
                                            <th>Entity</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedActivityLogs.map((log) => {
                                            const isDestructive = log.action.includes('delete') || log.action.includes('deactivate');
                                            const isCreate = log.action.includes('reactivate') || log.action.includes('create') || log.action.includes('add');
                                            const pillClass = isDestructive ? 'at-pill at-pill--danger' : isCreate ? 'at-pill at-pill--success' : 'at-pill at-pill--info';
                                            return (
                                                <tr key={log.id}>
                                                    <td>
                                                        <div className="at-primary">{formatDate(log.created_at)}</div>
                                                        <span className="at-secondary">{formatTime(log.created_at)}</span>
                                                    </td>
                                                    <td>
                                                        <div className="at-primary">{log.user_name || 'Unknown'}</div>
                                                        <span className="at-secondary">{log.user_email}</span>
                                                    </td>
                                                    <td>
                                                        <span className={pillClass}>
                                                            {log.action.replace(/_/g, ' ').toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {log.entity_type && (
                                                            <>
                                                                <div className="at-primary">{log.entity_name || `${log.entity_type} #${log.entity_id}`}</div>
                                                                <span className="at-secondary">{log.entity_type}</span>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td className="at-muted">
                                                        {log.details}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            {filteredActivityLogs.length > ACTIVITY_LOGS_PAGE_SIZE && (
                                <div className="admin-table-footer">
                                    <span>
                                        Showing {activityLogsStart + 1}–{Math.min(activityLogsStart + ACTIVITY_LOGS_PAGE_SIZE, filteredActivityLogs.length)} of {filteredActivityLogs.length}
                                    </span>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button type="button" className="btn btn-secondary" onClick={() => setActivityLogsPage((p) => Math.max(1, p - 1))} disabled={activityLogsPage <= 1} style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            Previous
                                        </button>
                                        <span>Page {activityLogsPage} of {activityLogsTotalPages}</span>
                                        <button type="button" className="btn btn-secondary" onClick={() => setActivityLogsPage((p) => Math.min(activityLogsTotalPages, p + 1))} disabled={activityLogsPage >= activityLogsTotalPages} style={{ padding: '6px 12px', fontSize: '13px' }}>
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                            </>
                        ) : (
                            <div className="admin-table-wrap" style={{ textAlign: 'center', padding: '60px', color: '#718096' }}>
                                <p style={{ fontSize: '18px', fontWeight: 500 }}>No activity logs yet</p>
                                <p>User actions will appear here</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Tool requests — sub-tab under Tools. Two kinds: access requests
                    (for catalog tools) and new-tool proposals. */}
                {activeTab === 'tools' && toolsSubTab === 'requests' && (
                    <div>
                        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            <h2 style={{ margin: 0 }}>Tool requests</h2>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => { refreshPendingRequestsCount(); if (reqKind === 'new_tool') fetchToolRequests(); }}
                            >
                                Refresh
                            </button>
                        </div>

                        <nav className="admin-subtabs" aria-label="Request kind" style={{ marginBottom: 16 }}>
                            {can('tool_access.resolve') && (
                                <button
                                    type="button"
                                    className={`admin-subtab ${reqKind === 'access' ? 'is-active' : ''}`}
                                    onClick={() => setReqKind('access')}
                                >
                                    Access requests
                                    {pendingAccessCount > 0 && (
                                        <span className="admin-subtab-badge">{pendingAccessCount}</span>
                                    )}
                                </button>
                            )}
                            {can('tool_proposals.publish') && (
                                <button
                                    type="button"
                                    className={`admin-subtab ${reqKind === 'new_tool' ? 'is-active' : ''}`}
                                    onClick={() => setReqKind('new_tool')}
                                >
                                    New-tool proposals
                                    {pendingNewToolCount > 0 && (
                                        <span className="admin-subtab-badge">{pendingNewToolCount}</span>
                                    )}
                                </button>
                            )}
                        </nav>

                        {reqKind === 'access' && can('tool_access.resolve') && (
                            <AdminToolAccessRequests
                                onCountsChange={({ pending }) => setPendingAccessCount(pending)}
                            />
                        )}

                        {reqKind === 'new_tool' && can('tool_proposals.publish') && (
                          <>
                        <p style={{ color: '#718096', marginBottom: '16px' }}>Users propose tools that are NOT yet in the catalog. Approve and optionally create the tool, or reject with a note.</p>
                        <div className="filter-bar" style={{ marginBottom: '12px' }}>
                            <input
                                type="text"
                                className="search-input"
                                placeholder="Search by tool name, requester name, or email…"
                                value={toolRequestsQuery}
                                onChange={(e) => setToolRequestsQuery(e.target.value)}
                                style={{ flex: 1 }}
                            />
                            {toolRequestsQuery && (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setToolRequestsQuery('')}
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                        <div className="admin-table-count" style={{ marginBottom: '8px' }}>
                            Showing {filteredToolRequests.length} of {toolRequests.length} request{toolRequests.length !== 1 ? 's' : ''}
                        </div>
                        {toolRequests.length === 0 ? (
                            <div className="admin-table-wrap" style={{ textAlign: 'center', padding: '48px', color: '#718096' }}>
                                No tool requests yet.
                            </div>
                        ) : filteredToolRequests.length === 0 ? (
                            <div className="admin-table-wrap" style={{ textAlign: 'center', padding: '48px', color: '#718096' }}>
                                No requests match your search.
                            </div>
                        ) : (
                            <div className="admin-table-wrap ut-tablewrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Requested by</th>
                                            <th>Tool name</th>
                                            <th>Description / Link</th>
                                            <th style={{ width: '160px' }}>Status</th>
                                            <th style={{ width: '120px' }}>Date</th>
                                            <th style={{ width: '120px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedToolRequests.map((r) => {
                                            const pillClass = (r.status === 'pending' || r.status === 'pending_manager' || r.status === 'pending_admin') ? 'at-pill at-pill--warning' : r.status === 'approved' ? 'at-pill at-pill--success' : 'at-pill at-pill--danger';
                                            const PROPOSAL_STAGE_LABEL = {
                                                pending_manager: 'Awaiting manager',
                                                pending_admin: 'Awaiting admin',
                                                approved: 'Added',
                                                rejected: 'Rejected',
                                            };
                                            const statusLabel = PROPOSAL_STAGE_LABEL[r.status] || r.status;
                                            const safeLink = r.link && /^https?:\/\//i.test(r.link) ? r.link : null;
                                            return (
                                                <tr key={r.id}>
                                                    <td>
                                                        <div className="at-primary">{r.requester_name}</div>
                                                        <span className="at-secondary">{r.requester_email}</span>
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            {r.icon_url && <img src={getImageUrl(r.icon_url)} alt="" style={{ width: '24px', height: '24px', objectFit: 'contain', borderRadius: '4px' }} />}
                                                            <span className="at-primary">{r.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="at-muted" style={{ maxWidth: '260px' }}>
                                                        {r.description && <div>{r.description}</div>}
                                                        {r.link && (
                                                            <div style={{ marginTop: '4px' }}>
                                                                {safeLink
                                                                    ? <a href={safeLink} target="_blank" rel="noopener noreferrer" style={{ color: '#3182CE' }}>{r.link}</a>
                                                                    : <span>{r.link}</span>
                                                                }
                                                            </div>
                                                        )}
                                                        {!r.description && !r.link && '—'}
                                                    </td>
                                                    <td>
                                                        <span className={pillClass}>{statusLabel}</span>
                                                        {r.admin_notes && <div className="at-secondary">{r.admin_notes}</div>}
                                                    </td>
                                                    <td className="at-muted">{formatDate(r.created_at)}</td>
                                                    <td>
                                                        {r.status === 'pending_admin' && (
                                                            <button type="button" className="btn btn-small btn-primary" onClick={() => setResolveRequestModal({ open: true, request: r, admin_notes: '', category_id: '' })}>
                                                                Add to catalog
                                                            </button>
                                                        )}
                                                        {r.status === 'pending_manager' && (
                                                            <span className="at-pill at-pill--info" style={{ fontSize: '11px' }}>With manager</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {filteredToolRequests.length > TOOL_REQUESTS_PAGE_SIZE && (
                                    <div className="admin-table-footer">
                                        <span>
                                            Showing {toolRequestsStart + 1}–{Math.min(toolRequestsStart + TOOL_REQUESTS_PAGE_SIZE, filteredToolRequests.length)} of {filteredToolRequests.length}
                                        </span>
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <button type="button" className="btn btn-secondary" onClick={() => setToolRequestsPage((p) => Math.max(1, p - 1))} disabled={toolRequestsPage <= 1} style={{ padding: '6px 12px', fontSize: '13px' }}>
                                                Previous
                                            </button>
                                            <span>Page {toolRequestsPage} of {toolRequestsTotalPages}</span>
                                            <button type="button" className="btn btn-secondary" onClick={() => setToolRequestsPage((p) => Math.min(toolRequestsTotalPages, p + 1))} disabled={toolRequestsPage >= toolRequestsTotalPages} style={{ padding: '6px 12px', fontSize: '13px' }}>
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                          </>
                        )}
                    </div>
                )}

                {/* Category Modal */}
                {/* Department Modal */}
                {departmentModal.open && (
                    <div className="modal-overlay" onClick={() => setDepartmentModal({ open: false, department: null })}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h2>{departmentModal.department ? 'Edit Department' : 'Add Department'}</h2>
                            <div className="form-group">
                                <label>Name</label>
                                <input 
                                    value={departmentForm.name} 
                                    onChange={(e) => setDepartmentForm({ ...departmentForm, name: e.target.value })} 
                                    placeholder="Engineering, Marketing, Sales..."
                                />
                            </div>
                            <div className="form-group">
                                <label>Description (Optional)</label>
                                <textarea 
                                    value={departmentForm.description} 
                                    onChange={(e) => setDepartmentForm({ ...departmentForm, description: e.target.value })} 
                                    placeholder="Brief description of the department..."
                                    rows="3"
                                />
                            </div>
                            <div className="form-actions">
                                <button className="btn btn-secondary" onClick={() => setDepartmentModal({ open: false, department: null })}>Cancel</button>
                                <button className="btn btn-primary" onClick={saveDepartmentForm}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Drawer */}
                <div
                    className={`ut-drawer-backdrop ${categoryModal.open ? 'is-open' : ''}`}
                    onClick={() => setCategoryModal({ open: false, category: null })}
                    aria-hidden={!categoryModal.open}
                />
                <aside
                    className={`ut-drawer tool-drawer ${categoryModal.open ? 'is-open' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label={categoryModal.category ? 'Edit Category' : 'Add Category'}
                    aria-hidden={!categoryModal.open}
                >
                    {categoryModal.open && (
                    <>
                        <header className="tool-drawer-head">
                            <h2 style={{ margin: 0, fontSize: '18px' }}>{categoryModal.category ? 'Edit Category' : 'Add Category'}</h2>
                            <button
                                type="button"
                                className="tool-drawer-close"
                                aria-label="Close"
                                onClick={() => setCategoryModal({ open: false, category: null })}
                            >×</button>
                        </header>
                        <div className="tool-drawer-body">
                            <div className="form-group">
                                <label>Name</label>
                                <input
                                    value={categoryForm.name}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                    placeholder="e.g. Marketing, Cloud & Infrastructure"
                                />
                            </div>
                            <div className="form-group">
                                <label>Icon (inline SVG)</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: '12px', alignItems: 'start' }}>
                                    <textarea
                                        value={categoryForm.icon}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                                        rows={8}
                                        placeholder={'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ...>...</svg>'}
                                        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12px', lineHeight: 1.45, resize: 'vertical', minHeight: '180px' }}
                                    />
                                    <div
                                        style={{
                                            width: '96px',
                                            height: '96px',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px',
                                            background: '#f8fafc',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#C0322B',
                                        }}
                                        aria-label="Icon preview"
                                    >
                                        {categoryForm.icon && categoryForm.icon.trim().startsWith('<') ? (
                                            <div
                                                dangerouslySetInnerHTML={{ __html: categoryForm.icon }}
                                                style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            />
                                        ) : categoryForm.icon ? (
                                            <span style={{ fontSize: '32px' }}>{categoryForm.icon}</span>
                                        ) : (
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>No preview</span>
                                        )}
                                    </div>
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '6px' }}>
                                    Paste an inline SVG. Use <code>stroke="currentColor"</code> so it inherits theme colour. The preview on the right updates as you type.
                                </div>
                            </div>
                        </div>
                        <footer className="tool-drawer-foot">
                            <button className="btn btn-secondary" onClick={() => setCategoryModal({ open: false, category: null })}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveCategoryForm}>
                                {categoryModal.category ? 'Save changes' : 'Add category'}
                            </button>
                        </footer>
                    </>
                    )}
                </aside>


                {/* Tool Drawer */}
                <div
                    className={`ut-drawer-backdrop ${toolModal.open ? 'is-open' : ''}`}
                    onClick={() => setToolModal({ open: false, tool: null })}
                    aria-hidden={!toolModal.open}
                />
                <aside
                    className={`ut-drawer tool-drawer ${toolModal.open ? 'is-open' : ''}`}
                    role="dialog"
                    aria-modal="true"
                    aria-label={toolModal.tool ? 'Edit Tool' : 'Add Tool'}
                    aria-hidden={!toolModal.open}
                >
                    {toolModal.open && (
                    <>
                        <header className="tool-drawer-head">
                            <h2 style={{ margin: 0, fontSize: '18px' }}>{toolModal.tool ? 'Edit Tool' : 'Add Tool'}</h2>
                            <button
                                type="button"
                                className="tool-drawer-close"
                                aria-label="Close"
                                onClick={() => setToolModal({ open: false, tool: null })}
                            >×</button>
                        </header>
                        <div className="tool-drawer-body">
                            <div className="form-group">
                                <label>Name</label>
                                <input value={toolForm.name} onChange={(e) => setToolForm({ ...toolForm, name: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea value={toolForm.description} onChange={(e) => setToolForm({ ...toolForm, description: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Icon</label>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <input 
                                            value={toolForm.icon_url} 
                                            onChange={(e) => setToolForm({ ...toolForm, icon_url: e.target.value })} 
                                            placeholder="/images/example.png or https://example.com/icon.png" 
                                            style={{ width: '100%' }}
                                        />
                                        <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                            Enter a local path (e.g., /images/gmail.png) or full URL (e.g., https://cdn.example.com/icon.png)
                                        </div>
                                    </div>
                                    {toolForm.icon_url && (
                                        <div style={{ 
                                            width: '48px', 
                                            height: '48px', 
                                            border: '1px solid #e2e8f0', 
                                            borderRadius: '6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#f7fafc',
                                            flexShrink: 0
                                        }}>
                                            <img 
                                                src={getImageUrl(toolForm.icon_url)} 
                                                alt="Icon preview" 
                                                style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = '<div style="font-size: 10px; color: #a0aec0; text-align: center;">Invalid</div>';
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Link</label>
                                <input value={toolForm.link} onChange={(e) => setToolForm({ ...toolForm, link: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select value={toolForm.category_id} onChange={(e) => setToolForm({ ...toolForm, category_id: e.target.value })}>
                                    <option value="">Select Category</option>
                                    {categories.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Type</label>
                                <select value={toolForm.type} onChange={(e) => setToolForm({ ...toolForm, type: e.target.value })}>
                                    {TOOL_TYPES.map(({ value, label }) => (
                                        <option key={value} value={value}>{label}</option>
                                    ))}
                                </select>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    Categorises what the tile points to. Used on the catalog sidebar and as a badge on tool cards.
                                </div>
                            </div>
                            {can('departments.manage') && (
                            <div className="form-group">
                                <label>Assign to Departments (Optional)</label>
                                <div style={{
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '6px',
                                    padding: '12px',
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    backgroundColor: '#f7fafc'
                                }}>
                                    {departments.length === 0 ? (
                                        <div style={{ color: '#a0aec0', fontSize: '13px', fontStyle: 'italic' }}>No departments available</div>
                                    ) : (
                                        departments.map(dept => {
                                            const isSelected = toolForm.department_ids.includes(dept.id);
                                            return (
                                                <label
                                                    key={dept.id}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px',
                                                        padding: '8px',
                                                        cursor: 'pointer',
                                                        borderRadius: '4px',
                                                        marginBottom: '4px',
                                                        backgroundColor: isSelected ? '#E9453E15' : 'transparent',
                                                        border: isSelected ? '1px solid #E9453E' : '1px solid transparent',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = 'white';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setToolForm({ ...toolForm, department_ids: [...toolForm.department_ids, dept.id] });
                                                            } else {
                                                                setToolForm({ ...toolForm, department_ids: toolForm.department_ids.filter(id => id !== dept.id) });
                                                            }
                                                        }}
                                                        style={{ 
                                                            width: '16px', 
                                                            height: '16px', 
                                                            cursor: 'pointer',
                                                            accentColor: '#E9453E'
                                                        }}
                                                    />
                                                    <span style={{ 
                                                        fontSize: '14px', 
                                                        color: isSelected ? '#E9453E' : '#2d3748',
                                                        fontWeight: isSelected ? 600 : 400
                                                    }}>
                                                        {dept.name}
                                                    </span>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                <div style={{ fontSize: '12px', color: '#718096', marginTop: '4px' }}>
                                    Select departments to assign this tool as a default. Users in these departments will see this tool.
                                </div>
                            </div>
                            )}
                        </div>
                        <footer className="tool-drawer-foot">
                            <button className="btn btn-secondary" onClick={() => setToolModal({ open: false, tool: null })}>Cancel</button>
                            <button className="btn btn-primary" onClick={saveToolForm}>Save</button>
                        </footer>
                    </>
                    )}
                </aside>

                {/* Action Item Modal */}
                {actionModal.open && (
                    <div className="modal-overlay" onClick={() => setActionModal({ open: false, action: null })}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h2>{actionModal.action ? 'Edit Action Item' : 'Add Action Item'}</h2>
                            <div className="form-group">
                                <label>Title</label>
                                <input value={actionForm.title} onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <ReactQuill
                                    theme="snow"
                                    value={actionForm.description}
                                    onChange={(value) => setActionForm({ ...actionForm, description: value })}
                                    modules={quillModules}
                                    formats={quillFormats}
                                />
                            </div>
                            <div className="form-actions">
                                <button className="btn btn-secondary" onClick={() => setActionModal({ open: false, action: null })}>Cancel</button>
                                <button className="btn btn-primary" onClick={saveActionForm}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Policy Modal */}
                {policyModal.open && (
                    <div className="modal-overlay" onClick={() => setPolicyModal({ open: false, policy: null })}>
                        <div className="modal" onClick={(e) => e.stopPropagation()}>
                            <h2>{policyModal.policy ? 'Edit Policy' : 'Add Policy'}</h2>
                            <div className="form-group">
                                <label>Name</label>
                                <input value={policyForm.name} onChange={(e) => setPolicyForm({ ...policyForm, name: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label>Link</label>
                                <input value={policyForm.link} onChange={(e) => setPolicyForm({ ...policyForm, link: e.target.value })} />
                            </div>
                            <div className="form-actions">
                                <button className="btn btn-secondary" onClick={() => setPolicyModal({ open: false, policy: null })}>Cancel</button>
                                <button className="btn btn-primary" onClick={savePolicyForm}>Save</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add to catalog Modal (admin-only action for pending_admin proposals) */}
                {resolveRequestModal.open && resolveRequestModal.request && (
                    <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => !resolving && setResolveRequestModal({ ...resolveRequestModal, open: false })}>
                        <div className="modal resolve-request-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', width: '100%', padding: 0, overflow: 'hidden' }}>
                            <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid #e2e8f0' }}>
                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#1a202c' }}>Add to catalog</h2>
                                <p style={{ margin: '6px 0 0', fontSize: '15px', fontWeight: 500, color: '#2d3748' }}>{resolveRequestModal.request.name}</p>
                                <span style={{ display: 'inline-block', marginTop: '8px', fontSize: '12px', color: '#718096', background: '#f7fafc', padding: '4px 10px', borderRadius: '6px' }}>From {resolveRequestModal.request.requester_name}</span>
                            </div>
                            <div style={{ padding: '20px 24px' }}>
                                <div className="form-group" style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '6px' }}>Category</label>
                                    <select value={resolveRequestModal.category_id} onChange={(e) => setResolveRequestModal({ ...resolveRequestModal, category_id: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
                                        <option value="">No category</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#4a5568', marginBottom: '6px' }}>Admin note <span style={{ fontWeight: 400, color: '#a0aec0' }}>(optional, visible to requester)</span></label>
                                    <textarea value={resolveRequestModal.admin_notes} onChange={(e) => setResolveRequestModal({ ...resolveRequestModal, admin_notes: e.target.value })} placeholder="e.g. Added to Engineering tools." rows={2} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', resize: 'vertical', minHeight: '64px' }} />
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', background: '#f7fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => setResolveRequestModal({ ...resolveRequestModal, open: false })} disabled={resolving}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleResolveRequest} disabled={resolving}>
                                    {resolving ? 'Adding...' : 'Add to catalog'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Department Managers Tab */}
                {activeTab === 'dept_managers' && <DepartmentManagersPanel />}

                </>
              ) : (
                <AdminAccessDenied />
              )}

                {/* Confirmation Modal */}
                <ConfirmModal
                    isOpen={confirmModal.open}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText}
                    isDangerous={confirmModal.isDangerous}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal({ ...confirmModal, open: false })}
                />
            </div>
        </AdminShell>
    );
};

export default AdminDashboard;
